import styled, { css } from 'styled-components';

interface IUserAvatarProps {
  $avatarSrc: string;
}

interface IDotAccomplished {
  $accomplished: boolean;
  $width: 'small' | 'medium' | 'large';
}

interface ITagProps {
  $color: string;
  $border: boolean;
}

interface ISizeDotsArea {
  $dots: number;
}

interface IPropsWidth {
  $width: 'small' | 'medium' | 'large';
}

interface IShowButton {
  $hover: boolean;
}

const SlideDotsProps = {
  small: css`
    width: 0.6rem;
    height: 0.6rem;
  `,

  medium: css`
    width: 0.8rem;
    height: 0.8rem;
  `,

  large: css`
    width: 1rem;
    height: 1rem;
  `,
};

const SlideDoneDotsProps = {
  small: css`
    width: 0.45rem;
    height: 0.45rem;
  `,

  medium: css`
    width: 0.65rem;
    height: 0.65rem;
  `,

  large: css`
    width: 0.85rem;
    height: 0.85rem;
  `,
};

const SlideButtonProps = {
  small: css`
    height: 1.4rem;
    width: 1.4rem;

    > svg {
      width: 0.8rem;
      stroke: #fff;
      stroke-width: 2.8;
      fill: none;
    }
  `,

  medium: css`
    width: 1.6rem;
    height: 1.6rem;

    > svg {
      width: 1rem;
      stroke: #fff;
      stroke-width: 3;
      fill: none;
    }
  `,

  large: css`
    width: 2rem;
    height: 2rem;

    > svg {
      width: 1.22rem;
      stroke: #fff;
      stroke-width: 4;
      fill: none;
    }
  `,
};

export const Container = styled.div<{ $hideBorderRadius: boolean }>`
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.8rem 1rem 0.6rem 1rem;
  border: ${props => (props.$hideBorderRadius ? 'none' : '2px solid #e2e2e2')};
  border-radius: 1.38rem;
  will-change: height;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const PostUserContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  > section {
    margin-left: 0.8rem;
    > h2 {
      font-family: 'RedHatDisplay-Bold';
      font-size: 1.11rem;
      line-height: 1.28rem;
      color: #323232;
    }

    > p {
      font-family: 'RedHatDisplay-Regular';
      font-size: 0.66rem;
    }
  }
`;

export const UserAvatar = styled.div<IUserAvatarProps>`
  width: 2.8rem;
  height: 2.8rem;
  max-width: 50px;
  max-height: 50px;
  min-width: 30px;
  min-height: 30px;
  border-radius: 50%;
  background: url('${props => props.$avatarSrc}') no-repeat center;
  background-size: cover;
`;

/**
 * Junta as tags e o menu de ações na ponta direita do cabeçalho.
 *
 * O menu não pode entrar no TagsContainer: lá ele viraria mais um item do
 * grid do celular e seria cortado pela regra que limita as tags a duas.
 */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

export const TagsContainer = styled.div`
  display: flex;
  gap: 0.8rem;

  @media (max-width: 1024px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;

    /* Mostra no máximo duas tags, para o cabeçalho não virar uma parede de
       etiquetas na tela estreita. As ações do autor não entram nesta conta:
       moram no menu, fora deste container. */
    & > div:nth-child(n + 3) {
      display: none;
    }
  }
`;

export const Tag = styled.div<ITagProps>`
  padding: 0.4rem;
  font-family: 'RedHatDisplay-Medium';
  font-size: 0.8rem;
  width: 6.5rem;
  border-radius: 2.7rem;
  border: ${props => (props.$border ? `2px solid ${props.$color}` : 'none')};
  background: ${props => (props.$border ? 'transparent' : props.$color)};
  color: ${props => (props.$border ? `${props.$color}` : '#fff')};
  text-align: center;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;

  > h1 {
    font-family: 'RedHatDisplay-ExtraBold';
    font-size: 1.66rem;
    line-height: 2rem;
    color: #1e88e5;
    margin-bottom: 0.8rem;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  > span {
    font-family: 'RedHatDisplay-Medium';
    font-size: 1rem;
    color: #323232;
    margin-bottom: 0.4rem;
    text-indent: 1rem; /* Adds paragraph indentation to the first line */

    &::first-letter {
      text-transform: uppercase;
    }
  }
`;

export const CarouselWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  aspect-ratio: 1 / 1;
  border-radius: 0.56rem;
  background: #f5f5f5;
`;

export const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease-in-out;
  height: 100%;
`;

export const CarouselSlide = styled.div`
  min-width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

/**
 * Marca vídeo que já está no ar mas ainda não passou pela avaliação humana.
 * Fica no canto superior para não competir com os controles do player.
 */
export const ReviewTag = styled.span`
  position: absolute;
  top: 0.6rem;
  left: 0.6rem;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.55rem;
  border-radius: 0.3rem;
  background: rgba(21, 101, 192, 0.9);
  color: #fff;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.7rem;
  line-height: 1.2;
  pointer-events: none;
`;

export const ButtonContainer = styled.div<IShowButton>`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  justify-content: space-between;
  top: 0;
  bottom: 0;
  z-index: 5;

  opacity: ${props => (props.$hover ? 1 : 0)};
  transition: opacity 0.3s ease;

  /* A camada cobre o slide inteiro só para captar o hover. Deixá-la clicável
     roubaria todo clique destinado ao vídeo, que fica embaixo. */
  pointer-events: none;

  button {
    pointer-events: auto;
    border-radius: 50%;
    position: absolute;
    width: 1.5rem;
    height: 1.5rem;
    background-color: #1e88e5;
    display: flex;
    align-items: center;
    justify-content: center;

    &:first-child {
      top: 50%;
      transform: translateY(-50%);
      left: 2%;
    }

    &:last-child {
      top: 50%;
      transform: translateY(-50%);
      right: 2%;
    }
  }
`;

export const Indicators = styled.div<IPropsWidth>`
  margin-top: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;

  > button {
    border-radius: 50%;
    background-color: #1e88e5;
    ${props => SlideButtonProps[props.$width || 'large']}
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &:first-child {
      margin-right: 0.5rem;
    }

    &:last-child {
      margin-left: 0.5rem;
    }
  }
`;

export const DotSlides = styled.div<ISizeDotsArea>`
  max-width: ${props => props.$dots * 1.1}rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(0.9rem, 1fr));
  grid-gap: 0.3rem;
  justify-content: center;
`;

export const Dot = styled.div<IPropsWidth>`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  ${props => SlideDotsProps[props.$width || 'small']}

  background-color: #e2e2e2;
  border-radius: 50%;

  position: relative;
`;

export const DotAccomplished = styled.div<IDotAccomplished>`
  ${props => SlideDoneDotsProps[props.$width || 'small']}
  border-radius: 50%;
  background-color: ${props => (props.$accomplished ? '#4CAF50' : '#e2e2e2')};
  transition: background-color 0.4s ease;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.8rem;
  > div {
    display: flex;
    gap: 0.8rem;
    align-items: center;
  }

  > span {
    font-family: 'RedHatDisplay-Regular';
    font-size: 0.86rem;
    line-height: 2rem;
    color: #323232;
    text-align: center;
  }
`;

export const CommentsButton = styled.button`
  display: flex;
  align-items: center;

  span {
    margin-left: 0.2rem;
    font-family: 'RedHatDisplay-Regular';
    font-size: 1.2rem;
    color: #323232;
  }

  &:hover {
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.08);
    }
    100% {
      transform: scale(1);
    }
  }
`;

export const LikeButton = styled.button`
  display: flex;
  align-items: center;

  span {
    margin-left: 0.2rem;
    font-family: 'RedHatDisplay-Regular';
    font-size: 1.2rem;
    color: #323232;
  }

  &:hover {
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.08);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes wave-arm {
    0%,
    100% {
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(-10deg);
    }
  }

  @keyframes bounce-hand {
    0%,
    100% {
      transform: rotate(0deg);
      transform: translateY(0);
    }
    50% {
      transform: translateY(-4px);
      transform: rotate(-5deg);
    }
  }

  /* Quando a classe 'animate-thumbs' estiver ativa, aplica as animações */
  .animate-thumbs .thumb-arm {
    transform-origin: bottom center;
    animation: bounce-hand 0.3s ease-in-out;
  }

  .animate-thumbs .thumb-hand {
    transform-origin: center;
    animation: bounce-hand 0.3s ease-in-out;
  }
`;

export const CommentsContainer = styled.div<{ $closing?: boolean }>`
  overflow: hidden;
  transform-origin: top center;
  animation: ${({ $closing }) =>
      $closing ? 'comments-slide-out' : 'comments-slide-in'}
    0.28s ease forwards;

  @keyframes comments-slide-in {
    from {
      opacity: 0;
      transform: translateY(-10px) scaleY(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0px) scaleY(1);
    }
  }

  @keyframes comments-slide-out {
    from {
      opacity: 1;
      transform: translateY(0px) scaleY(1);
    }
    to {
      opacity: 0;
      transform: translateY(-10px) scaleY(0.96);
    }
  }
`;

export const CommentsWrapper = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 1.22rem;
  border: 2px solid #e2e2e2;
  padding: 1.22rem 0;

  > h1 {
    margin-bottom: 1.22rem;
    font-family: 'RedHatDisplay-ExtraBold';
    font-size: 1.2rem;
    line-height: 2rem;
    color: #323232;
  }
`;

export const CommentInput = styled.div`
  overflow: hidden;
  width: 100%;
  margin: 1.2rem 0;

  > form {
    width: 100%;
    display: flex;
    align-items: center;

    > .primary-button {
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;
      height: 2.83rem;
      width: 2.83rem;
      margin-left: 1rem;
      border-radius: 0.6rem;
      box-shadow: 0px 3px 15px rgba(255, 152, 0, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        transition: stroke 0.3s ease;
      }

      > div {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
`;

export const InputWrapper = styled.div`
  margin-left: 1rem;
  flex: 1;
`;

export const CommentContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 1rem;
  border-top: 1px solid #e9e9e9;
  border-bottom: 1px solid #e9e9e9;
`;

export const CommentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.8rem;

  > h1 {
    font-family: 'RedHatDisplay-ExtraBold';
    font-size: 0.8rem;
    color: #323232;
  }

  > span {
    font-family: 'RedHatDisplay-Regular';
    font-size: 0.5rem;
    color: #323232;
  }

  > p {
    margin-top: 0.3rem;
    font-family: 'RedHatDisplay-Regular';
    font-size: 0.8rem;
    color: #323232;
  }
`;
export const UserAvatarComments = styled.div<IUserAvatarProps>`
  width: 2.78rem;
  height: 2.78rem;
  max-width: 50px;
  max-height: 50px;
  min-width: 20px;
  min-height: 20px;
  border-radius: 50%;
  background: url('${props => props.$avatarSrc}') no-repeat center;
  background-size: cover;
`;

/**
 * Entrada para a edição, visível só para o autor.
 *
 * Fica entre as tags e não num menu suspenso: é uma ação só, e esconder ação
 * única atrás de três pontinhos custa um clique sem economizar nada.
 */

/**
 * Faixa de confirmação da exclusão.
 *
 * Fica no corpo do card, não num alerta do navegador: é preciso caber a frase
 * que diz o que se perde, e window.confirm não tem onde colocá-la nem como
 * distinguir a ação de risco do cancelar.
 */
export const DeleteConfirmBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem;
  margin: 0 1.5rem 1rem;
  padding: 0.9rem 1rem;
  border: 1px solid #f2c4c3;
  border-radius: 0.8rem;
  background: rgba(255, 46, 62, 0.05);

  > p {
    flex: 1 1 16rem;
    font-family: 'RedHatDisplay-Regular';
    font-size: 0.85rem;
    color: #63646a;

    > strong {
      font-family: 'RedHatDisplay-Bold';
      color: #323232;
    }
  }
`;

export const ConfirmDeleteButton = styled.button`
  padding: 0.55rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: #e53935;
  color: #fff;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.8rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: progress;
  }
`;

export const CancelDeleteButton = styled.button`
  border: none;
  background: none;
  color: #63646a;
  font-family: 'RedHatDisplay-Medium';
  font-size: 0.8rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #323232;
  }
`;
